import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import { getAuthUser } from '../utils/auth';

const statusLabels = {
  REQUESTED: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  APPROVED: { label: 'Aprobada', color: 'bg-green-100 text-green-700' },
  REJECTED: { label: 'Rechazada', color: 'bg-red-100 text-red-700' },
};

const typeLabels = {
  VACATION: 'Vacaciones',
  SICK: 'Enfermedad',
  UNPAID: 'Sin sueldo',
  PERSONAL: 'Personal',
  OTHER: 'Otra',
};

export default function AbsencesBoard() {
  const authUser = getAuthUser();
  const isEmployee = authUser?.role === 'EMPLOYEE';
  const isAdmin = ['ADMIN', 'HR'].includes(authUser?.role);
  const isHr = authUser?.role === 'HR';
  const currentEmployeeId = authUser?.employeeId ?? null;
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterError, setFilterError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({
    employeeId: '',
    type: 'VACATION',
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [dateError, setDateError] = useState(null);

  const employeeMap = useMemo(() => {
    return new Map(employees.map((employee) => [employee.id, employee]));
  }, [employees]);

  const hrDepartmentId = useMemo(() => {
    if (!isHr || !currentEmployeeId) {
      return null;
    }
    const hrEmployee = employees.find((employee) => employee.id === currentEmployeeId);
    return hrEmployee?.departmentId ?? null;
  }, [employees, isHr, currentEmployeeId]);

  const scopedEmployees =
    isHr && hrDepartmentId
      ? employees.filter((employee) => employee.departmentId === hrDepartmentId)
      : employees;

  const rows = useMemo(() => {
    const baseRows = absences.map((absence) => ({
      ...absence,
      employeeName:
        absence.employee?.firstName || absence.employee?.lastName
          ? `${absence.employee?.firstName ?? ''} ${absence.employee?.lastName ?? ''}`.trim()
          : employeeMap.has(absence.employeeId)
            ? `${employeeMap.get(absence.employeeId).firstName} ${employeeMap.get(absence.employeeId).lastName}`
            : absence.employeeName || `Empleado ${absence.employeeId}`,
    }));
    if (!isAdmin) {
      return baseRows;
    }
    return baseRows.filter((absence) => {
      if (isHr && hrDepartmentId) {
        const employee = employeeMap.get(absence.employeeId);
        if (!employee || employee.departmentId !== hrDepartmentId) {
          return false;
        }
      }
      if (employeeFilter && String(absence.employeeId) !== employeeFilter) {
        return false;
      }
      if (typeFilter && absence.type !== typeFilter) {
        return false;
      }
      if (statusFilter && absence.status !== statusFilter) {
        return false;
      }
      if (dateFrom && absence.endDate < dateFrom) {
        return false;
      }
      if (dateTo && absence.startDate > dateTo) {
        return false;
      }
      return true;
    });
  }, [
    absences,
    employeeMap,
    isAdmin,
    isHr,
    hrDepartmentId,
    employeeFilter,
    typeFilter,
    statusFilter,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    const loadAbsences = async () => {
      setIsLoading(true);
      setError(null);
      setFilterError(null);
      if (isAdmin && dateFrom && dateTo && dateFrom > dateTo) {
        setFilterError('La fecha "desde" no puede ser posterior a "hasta".');
        setAbsences([]);
        setIsLoading(false);
        return;
      }
      try {
        if (isEmployee && currentEmployeeId) {
          const [absencesResponse, employeeResponse] = await Promise.all([
            axios.get('/absences'),
            axios.get(`/employees/${currentEmployeeId}`),
          ]);
          setAbsences(absencesResponse.data);
          setEmployees([employeeResponse.data]);
        } else {
          const params = {};
          if (dateFrom) {
            params.from = dateFrom;
          }
          if (dateTo) {
            params.to = dateTo;
          }
          const [absencesResponse, employeesResponse] = await Promise.all([
            axios.get('/absences', { params }),
            axios.get('/employees'),
          ]);
          setAbsences(absencesResponse.data);
          setEmployees(employeesResponse.data);
        }
      } catch (err) {
        const message =
          err.response?.data?.message || 'No se pudieron cargar las ausencias.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadAbsences();
  }, [isEmployee, currentEmployeeId, isAdmin, dateFrom, dateTo]);

  useEffect(() => {
    if (isEmployee && currentEmployeeId) {
      setForm((prev) => ({ ...prev, employeeId: currentEmployeeId.toString() }));
    }
  }, [isEmployee, currentEmployeeId]);

  const reloadAbsences = async () => {
    const params = {};
    if (isAdmin && dateFrom) {
      params.from = dateFrom;
    }
    if (isAdmin && dateTo) {
      params.to = dateTo;
    }
    const response = await axios.get('/absences', { params });
    setAbsences(response.data);
  };

  const updateStatus = async (absenceId, status) => {
    try {
      if (status === 'APPROVED') {
        await axios.patch(`/absences/${absenceId}/approve`);
      } else {
        await axios.patch(`/absences/${absenceId}/reject`);
      }
      await reloadAbsences();
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo actualizar la solicitud.';
      setError(message);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'startDate' || name === 'endDate') {
      const nextStart = name === 'startDate' ? value : form.startDate;
      const nextEnd = name === 'endDate' ? value : form.endDate;
      if (nextStart && nextEnd && nextStart > nextEnd) {
        setDateError('La fecha de inicio no puede ser posterior a la fecha de fin.');
      } else {
        setDateError(null);
      }
    }
  };

  const resetForm = () => {
    setForm({
      employeeId: '',
      type: 'VACATION',
      startDate: '',
      endDate: '',
      reason: '',
    });
    setEditingId(null);
    setDateError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    if (form.startDate && form.endDate && form.startDate > form.endDate) {
      setDateError('La fecha de inicio no puede ser posterior a la fecha de fin.');
      setIsSaving(false);
      return;
    }

    const resolvedEmployeeId = isEmployee ? currentEmployeeId : Number(form.employeeId);
    if (!resolvedEmployeeId) {
      setError('Usuario sin empleado asociado.');
      setIsSaving(false);
      return;
    }

    const payload = {
      employeeId: resolvedEmployeeId,
      type: form.type,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason || undefined,
    };

    try {
      if (editingId) {
        await axios.patch(`/absences/${editingId}`, payload);
      } else {
        await axios.post('/absences', payload);
      }
      await reloadAbsences();
      resetForm();
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo guardar la ausencia.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (absence) => {
    setEditingId(absence.id);
    setForm({
      employeeId: absence.employeeId,
      type: absence.type,
      startDate: absence.startDate,
      endDate: absence.endDate,
      reason: absence.reason || '',
    });
    if (absence.startDate && absence.endDate && absence.startDate > absence.endDate) {
      setDateError('La fecha de inicio no puede ser posterior a la fecha de fin.');
    } else {
      setDateError(null);
    }
  };

  const handleDelete = async (absenceId) => {
    const confirmDelete = window.confirm('¿Seguro que deseas eliminar esta ausencia?');
    if (!confirmDelete) {
      return;
    }
    setIsDeletingId(absenceId);
    setError(null);
    try {
      await axios.delete(`/absences/${absenceId}`);
      await reloadAbsences();
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo eliminar la ausencia.';
      setError(message);
    } finally {
      setIsDeletingId(null);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Ausencias</h1>
        <p className="text-sm text-gray-500">Gestion de solicitudes en modo mock.</p>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}
      {filterError && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
          {filterError}
        </div>
      )}

      {isAdmin && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="absenceFrom">
                Desde
              </label>
              <input
                id="absenceFrom"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="absenceTo">
                Hasta
              </label>
              <input
                id="absenceTo"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="absenceEmployee">
                Empleado
              </label>
              <select
                id="absenceEmployee"
                value={employeeFilter}
                onChange={(event) => setEmployeeFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Todos</option>
                {scopedEmployees.map((employee) => (
                  <option key={employee.id} value={String(employee.id)}>
                    {employee.firstName} {employee.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="absenceType">
                Tipo
              </label>
              <select
                id="absenceType"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Todos</option>
                {Object.keys(typeLabels).map((type) => (
                  <option key={type} value={type}>
                    {typeLabels[type]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="absenceStatus">
                Estado
              </label>
              <select
                id="absenceStatus"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Todos</option>
                {Object.keys(statusLabels).map((status) => (
                  <option key={status} value={status}>
                    {statusLabels[status].label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800">
          {editingId ? 'Editar ausencia' : 'Nueva ausencia'}
        </h2>
        <p className="text-xs text-gray-500">Completa la informacion requerida.</p>
        <form onSubmit={handleSubmit} className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-gray-600">Empleado</label>
            {isEmployee ? (
              <input
                type="text"
                disabled
                value={
                  employeeMap.has(currentEmployeeId)
                    ? `${employeeMap.get(currentEmployeeId).firstName} ${employeeMap.get(currentEmployeeId).lastName}`
                    : 'Empleado asignado'
                }
                className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-500"
              />
            ) : (
              <select
                name="employeeId"
                value={form.employeeId}
                onChange={handleFormChange}
                required
                className="mt-1 w-full rounded-lg border border-gray-200 p-2"
              >
                <option value="">Selecciona</option>
                {scopedEmployees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.firstName} {employee.lastName}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Tipo</label>
            <select
              name="type"
              value={form.type}
              onChange={handleFormChange}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            >
              {Object.keys(typeLabels).map((type) => (
                <option key={type} value={type}>
                  {typeLabels[type]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Inicio</label>
            <input
              type="date"
              name="startDate"
              value={form.startDate}
              onChange={handleFormChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Fin</label>
            <input
              type="date"
              name="endDate"
              value={form.endDate}
              onChange={handleFormChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-semibold text-gray-600">Motivo</label>
            <textarea
              name="reason"
              value={form.reason}
              onChange={handleFormChange}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
              rows="3"
              placeholder="Opcional"
            />
          </div>
          {dateError && (
            <div className="md:col-span-2 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
              {dateError}
            </div>
          )}
          <div className="md:col-span-2 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60"
            >
              {isSaving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear ausencia'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-gray-200 px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Cargando ausencias...</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Colaborador
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Periodo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((absence) => {
                const statusInfo = statusLabels[absence.status] ?? statusLabels.REQUESTED;
                return (
                  <tr key={absence.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {absence.employeeName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {typeLabels[absence.type] || absence.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {absence.startDate} - {absence.endDate}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${statusInfo.color}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap gap-2">
                        {!isEmployee ? (
                          absence.status === 'REQUESTED' ? (
                            <>
                              <button
                                onClick={() => updateStatus(absence.id, 'APPROVED')}
                                className="rounded bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700"
                              >
                                Aprobar
                              </button>
                              <button
                                onClick={() => updateStatus(absence.id, 'REJECTED')}
                                className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
                              >
                                Rechazar
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">Sin acciones</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">Sin acciones</span>
                        )}
                        <button
                          onClick={() => handleEdit(absence)}
                          disabled={absence.status !== 'REQUESTED'}
                          className="rounded bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Editar
                        </button>
                        {!isEmployee && (
                          <button
                            onClick={() => handleDelete(absence.id)}
                            disabled={isDeletingId === absence.id}
                            className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                          >
                            {isDeletingId === absence.id ? 'Eliminando...' : 'Eliminar'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
